import { Button, ButtonTheme } from 'components/Button';
import { Card } from 'components/Card';
import { CardRow } from 'components/CardRow';
import { TextField } from 'components/Field/TextField';
import { ICONS } from 'components/Icon';
import { ModalScreenProps, ModalScreens } from 'components/Navigation/types';
import { NavigationHeader } from 'components/NavigationHeader';
import { PageScrollView } from 'components/Page';
import { useConfirm, useConfirmUnsaved } from 'hooks/useConfirm';
import { useSelector } from 'hooks/useSelector';
import {
  filterArrayEmpty,
  formatCurrency,
  parseCurrency,
  randomKey,
} from 'lib/riskbase';
import { pluralise } from 'lib/pluralise';
import { cloneDeep, difference, intersection, isEqual, sortBy } from 'lodash';
import React, { useCallback, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import {
  doFieldSettingsGroupDelete,
  doFieldSettingsGroupSave,
  doFieldSettingsGroupSaved,
  doFieldSettingsSetGroupId,
  doFieldSettingsSetFieldId,
  doFieldSettingsGroupAddGroup,
  doFieldSettingsSetLiteGroupId,
} from 'store/actions';
import {
  NAVIGATE_MODAL,
  NAVIGATE_MODAL_BACK,
  eventFieldSettingUpdateGroupCheck,
  eventReorderScreenDone,
} from 'store/app/actions';
import {
  selectOrganisationFieldSettings,
  selectFieldSettingsGroupId,
  selectFieldSettingsSectionId,
  selectOrganisationFieldSettingsResultCount,
  selectFieldSettingsLiteGroupId,
  selectFieldSettingsGroupCheckTypes,
} from 'store/remote/selectors';
import { FieldSettingsGroup, FieldSettingsTypes } from 'store/remote/types';
import { FieldTypeKey, FIELD_ICON_SIZE } from 'types/types';
import { selectUserIsSupport } from 'store/auth/selectors';
import { useEsc } from 'hooks/useEsc';
import { theme } from 'lib/theme';
import { useForceUpdate } from 'hooks/useForceUpdate';
import { useAlert } from 'hooks/useAlert';
import { doAddEvent, doRemoveEvents } from 'lib/events';
import { View } from 'react-native';
import { ViewWithSeparators } from 'components/ViewWithSeparators';
import { useScreenSize } from 'hooks/useScreenSize';
import { ScreenSize } from 'lib/constants';
import { Txt } from 'components/Txt';

type NavigationRowField = {
  label: string;
  label2: string;
  id: string;
  value: string;
  icon?: ICONS;
  typeKey: FieldTypeKey;
  childGroupId: string | null;
  identifier: string;
};

type State = {
  group: FieldSettingsGroup;
};

export const FieldSettingsGroupModal: React.FC<
  ModalScreenProps<ModalScreens.FieldSettingsGroup>
> = ({ navigation, route }) => {
  const dispatch = useDispatch();
  const screenSize = useScreenSize();
  const confirm = useConfirm();
  const confirmUnsaved = useConfirmUnsaved();
  const alert = useAlert();
  const forceUpdate = useForceUpdate();

  const eventKeyFieldSettingUpdateGroupCheck = React.useRef(randomKey());

  const isSupport = useSelector(selectUserIsSupport);
  // const organisationId = useSelector(selectRemoteOrganisationId)!;
  const fieldSettings = useSelector(selectOrganisationFieldSettings);
  const resultsCount = useSelector(selectOrganisationFieldSettingsResultCount);

  const { isLite: rawIsLite, newData } = route.params;

  const groupCheckTypes = useSelector(selectFieldSettingsGroupCheckTypes);
  const sectionId = useSelector(selectFieldSettingsSectionId)!;
  const rawGroupId = useSelector(selectFieldSettingsGroupId);
  const rawLiteGroupId = useSelector(selectFieldSettingsLiteGroupId);
  const groupId = rawIsLite ? rawLiteGroupId : rawGroupId;
  const group = useMemo<FieldSettingsGroup>(
    () =>
      (groupId ? fieldSettings.groups[groupId] : undefined) || {
        fieldIds: [],
        id: '',
        name: '',
        nameSingular: '',
        repeatable: rawIsLite ? true : !!newData?.repeatable,
        newCost: null,
        existingCost: null,
        identifier: '',
        lite: !!rawIsLite,
        checks: {},
        disableAutoId: true,
        allowPdfDownload: false,
      },
    [fieldSettings.groups, groupId, rawIsLite, newData?.repeatable],
  );

  const section = Object.values(fieldSettings.sections).find(
    (orgSection) => orgSection.id === sectionId,
  )!;
  const editable = fieldSettings.editable && !section.managedByOrganisation;

  const afterSaveRef = React.useRef<
    ((savedGroupId: string) => void) | undefined
  >(undefined);
  const dataRef = React.useRef(cloneDeep(group));

  const [resetDataI, setResetDataI] = React.useState(0);
  const resetDataIRef = React.useRef(resetDataI);
  if (resetDataIRef.current !== resetDataI) {
    dataRef.current = cloneDeep(group);
    resetDataIRef.current = resetDataI;
  } else {
    // Add / remove fields from ref when they're added / deleted from state
    const newFieldIds = difference(group.fieldIds, dataRef.current.fieldIds);
    if (newFieldIds.length) {
      dataRef.current.fieldIds.push(...newFieldIds);
    }
    dataRef.current.fieldIds = intersection(
      dataRef.current.fieldIds,
      group.fieldIds,
    );
  }

  const childGroupIds = dataRef.current.fieldIds.reduce<string[]>(
    (acc, fldId) => {
      const fld = fieldSettings.fields[fldId];
      if (fld?.childGroupId) {
        acc.push(fld.childGroupId);
      }
      return acc;
    },
    [],
  );

  const availableChildGroups = Object.values(fieldSettings.groups).filter(
    (g) =>
      g.id !== groupId &&
      g.repeatable &&
      section.groupIds.includes(g.id) &&
      !childGroupIds.includes(g.id),
  );

  const checkIsDirty = React.useCallback(
    () => !isEqual(group, dataRef.current),
    [group],
  );
  const isDirty = React.useRef(false);
  isDirty.current = checkIsDirty();
  const isDirtyVar = isDirty.current;

  const handleDirtyUpdate = React.useCallback(() => {
    if (isDirty.current !== checkIsDirty()) {
      forceUpdate();
    }
  }, [checkIsDirty, forceUpdate]);

  // const [showOrganisationUsage, setShowOrganisationUsage] = React.useState(
  //   section.usedByOrganisations.length < 25,
  // );

  /** EDIT */

  const canSave = React.useCallback(
    (): boolean => !(!groupId && !dataRef.current.name),
    [groupId],
  );
  const showCannotSaveAlert = React.useCallback((): void => {
    alert({ text: 'Please enter a group name' });
  }, [alert]);

  const handleChange = <T extends keyof State['group']>(
    name: T,
    value: State['group'][T],
    doForceUpdate?: boolean,
  ) => {
    dataRef.current[name] = value;
    if (doForceUpdate) {
      forceUpdate();
    } else {
      handleDirtyUpdate();
    }
  };

  const handleChangeCost = (
    name: 'newCost' | 'existingCost',
    value: string,
  ) => {
    dataRef.current[name] = value.trim() !== '' ? parseCurrency(value) : null;
    handleDirtyUpdate();
  };

  const handleSave = React.useCallback(
    (afterSave: (savedGroupId: string) => void) => {
      if (!canSave()) {
        showCannotSaveAlert();
        return;
      }
      if (!groupId || isDirty.current) {
        afterSaveRef.current = afterSave;
        dispatch(
          groupId
            ? doFieldSettingsGroupSave({
                id: groupId,
                isLite: group.lite,
                data: {
                  name: dataRef.current.name,
                  nameSingular: dataRef.current.nameSingular,
                  fieldIds: dataRef.current.fieldIds,
                  newCost: dataRef.current.newCost,
                  existingCost: dataRef.current.existingCost,
                  identifier: dataRef.current.identifier,
                  checks: dataRef.current.checks,
                  disableAutoId: dataRef.current.disableAutoId,
                  allowPdfDownload: dataRef.current.allowPdfDownload,
                },
              })
            : doFieldSettingsGroupSave({
                id: undefined,
                isLite: group.lite,
                data: {
                  sectionId,
                  name: dataRef.current.name,
                  nameSingular: dataRef.current.nameSingular,
                  repeatable: dataRef.current.repeatable,
                  newCost: dataRef.current.newCost,
                  existingCost: dataRef.current.existingCost,
                  identifier: dataRef.current.identifier,
                  checks: dataRef.current.checks,
                  disableAutoId: dataRef.current.disableAutoId,
                  allowPdfDownload: dataRef.current.allowPdfDownload,
                },
              }),
        );
        return;
      }
      afterSave(groupId);
    },
    [canSave, dispatch, group.lite, groupId, sectionId, showCannotSaveAlert],
  );

  const handleDelete = React.useCallback(() => {
    confirm({
      destructive: true,
      confirmText: 'Delete',
      title: `Delete ${group.lite ? 'Lite Group' : 'Group'}`,
      text: `All fields and results will be deleted${pluralise(
        section.usedByOrganisations.length,
        '\n\nAnother organisation shares this group; continuing will delete it for them as well',
        '\n\n{x} other organisations share this group; continuing will delete it for them as well',
        '',
      )}\n\nThis cannot be undone`,
      onConfirm: () =>
        dispatch(
          doFieldSettingsGroupDelete({
            id: groupId!,
          }),
        ),
    });
  }, [
    confirm,
    dispatch,
    groupId,
    group.lite,
    section.usedByOrganisations.length,
  ]);

  /** FIELDS */

  const handleAddField = (type: FieldTypeKey, multiSelect?: boolean) => {
    if (!groupId) {
      handleSave(() => {
        dispatch(doFieldSettingsSetFieldId(undefined));
        dispatch(
          NAVIGATE_MODAL({
            screen: ModalScreens.FieldSettingsField,
            params: {
              newData: {
                type,
                multiSelect,
              },
            },
          }),
        );
      });
      return;
    }
    dispatch(doFieldSettingsSetFieldId(undefined));
    dispatch(
      NAVIGATE_MODAL({
        screen: ModalScreens.FieldSettingsField,
        params: {
          newData: {
            type,
            multiSelect,
          },
        },
      }),
    );
  };

  const handlePressField = useCallback(
    (fieldId: string) => () => {
      dispatch(doFieldSettingsSetFieldId(fieldId));
      dispatch(
        NAVIGATE_MODAL({
          screen: ModalScreens.FieldSettingsField,
          params: {},
        }),
      );
    },
    [dispatch],
  );

  const handlePressLightGroupField = React.useCallback(
    (childGroupId: string | null) => () => {
      if (childGroupId) {
        dispatch(doFieldSettingsSetLiteGroupId(childGroupId));
        dispatch(
          NAVIGATE_MODAL({
            screen: ModalScreens.FieldSettingsLiteGroup,
            params: {
              isLite: true,
              newData: { repeatable: true },
            },
          }),
        );
      }
    },
    [dispatch],
  );

  const handleAddLiteRepeatableGroup = () => {
    if (!groupId) {
      handleSave(() => {
        dispatch(doFieldSettingsSetLiteGroupId(undefined));
        dispatch(
          NAVIGATE_MODAL({
            screen: ModalScreens.FieldSettingsLiteGroup,
            params: {
              isLite: true,
              newData: {
                repeatable: true,
              },
            },
          }),
        );
      });
      return;
    }
    dispatch(doFieldSettingsSetLiteGroupId(undefined));
    dispatch(
      NAVIGATE_MODAL({
        screen: ModalScreens.FieldSettingsLiteGroup,
        params: {
          isLite: true,
          newData: {
            repeatable: true,
          },
        },
      }),
    );
  };

  const handleAddChildRepeatableGroup = (childGroupId: string) => {
    const childGroup = fieldSettings.groups[childGroupId];
    if (childGroup) {
      if (!groupId) {
        handleSave((savedGroupId) => {
          confirm({
            title: `Add ${childGroup.name}`,
            text: `Once added, you'll be able to associate ${childGroup.name.toLowerCase()} with this group`,
            confirmText: 'Add',
            onConfirm: () =>
              dispatch(
                doFieldSettingsGroupAddGroup({
                  id: savedGroupId,
                  childGroupId,
                }),
              ),
          });
        });
        return;
      }
      confirm({
        title: `Add ${childGroup.name}`,
        text: `Once added, you'll be able to associate ${childGroup.name.toLowerCase()} with this group`,
        confirmText: 'Add',
        onConfirm: () =>
          dispatch(doFieldSettingsGroupAddGroup({ id: groupId, childGroupId })),
      });
    }
  };

  const { fieldIds } = dataRef.current;
  const navigationRowFields = React.useMemo<NavigationRowField[]>(
    () =>
      fieldIds
        .map((fieldId) => {
          const fld = fieldSettings.fields[fieldId];
          if (!fld) {
            return null;
          }
          const type = FieldSettingsTypes[fld.type];
          const childGroup = fld.childGroupId
            ? fieldSettings.groups[fld.childGroupId]
            : null;
          return {
            id: fld.id,
            icon: fld.multiSelect
              ? type.iconMultiSelect || type.icon
              : type.icon,
            label: fld.childGroupId
              ? fieldSettings.groups[fld.childGroupId]?.name || fld.name
              : fld.name,
            typeKey: type.id,
            childGroupId: fld.childGroupId,
            label2: [
              childGroup
                ? pluralise(
                    childGroup.fieldIds.length,
                    '1 Field',
                    '{x} Fields',
                    'No Fields',
                  )
                : null,
              pluralise(fld.options.length, '1 Option', '{x} Options', ''),
              fld.multiSelect ? 'Multi Select' : '',
              fld.isLocation ? 'Identifies Location' : '',
            ]
              .filter(filterArrayEmpty)
              .join(' / '),
            value:
              type.id === FieldTypeKey.grouplight
                ? pluralise(
                    (childGroup?.fieldIds || []).reduce<number>(
                      (acc, childFieldId) =>
                        Object.values(
                          resultsCount?.[childFieldId] || {},
                        ).reduce((sum, count) => sum + count, acc),
                      0,
                    ),
                    '1 Result',
                    '{x} Results',
                    '',
                  )
                : pluralise(
                    Object.values(resultsCount?.[fieldId] || {}).reduce(
                      (sum, count) => sum + count,
                      0,
                    ),
                    '1 Result',
                    '{x} Results',
                    '',
                  ),
            identifier: fld.identifier,
          };
        })
        .filter(filterArrayEmpty),
    [fieldIds, fieldSettings.fields, fieldSettings.groups, resultsCount],
  );

  // const usageByOrganisation = React.useMemo<
  //   Array<{
  //     countResults: number;
  //     id: string;
  //     name: string;
  //   }>
  // >(
  //   () =>
  //     [
  //       { id: organisationId, name: 'This Organisation' },
  //       ...orderBy(section.usedByOrganisations, 'name'),
  //     ].map((usedByOrganisation) => ({
  //       id: usedByOrganisation.id,
  //       name: usedByOrganisation.name,
  //       countResults: group.fieldIds.reduce((acc, fieldId) => {
  //         const field = fieldSettings.fields[fieldId];
  //         return !field
  //           ? acc
  //           : field.type === FieldTypeKey.grouplight && field.childGroupId
  //           ? (fieldSettings.groups[field.childGroupId]?.fieldIds || []).reduce(
  //               (acc1, childFieldId) =>
  //                 acc1 +
  //                 (resultsCount?.[childFieldId]?.[usedByOrganisation.id] || 0),
  //               acc,
  //             )
  //           : acc + (resultsCount?.[fieldId]?.[usedByOrganisation.id] || 0);
  //       }, 0),
  //     })),
  //   [
  //     fieldSettings.fields,
  //     fieldSettings.groups,
  //     group.fieldIds,
  //     organisationId,
  //     resultsCount,
  //     section.usedByOrganisations,
  //   ],
  // );

  // Navigate back when id given but no state.group.id; the group was deleted
  React.useEffect(() => {
    if (groupId && !group.id) {
      dispatch(NAVIGATE_MODAL_BACK());
    }
  }, [dispatch, group.id, groupId]);

  const handleClose = React.useCallback(() => {
    if (isDirty.current) {
      confirmUnsaved({
        onSave: () => handleSave(() => dispatch(NAVIGATE_MODAL_BACK())),
        onDiscard: () => dispatch(NAVIGATE_MODAL_BACK()),
      });
      return;
    }
    dispatch(NAVIGATE_MODAL_BACK());
  }, [confirmUnsaved, dispatch, handleSave]);

  const reorderEventKey = React.useRef(randomKey());

  React.useEffect(() => {
    const eventNumbers = [
      doAddEvent(doFieldSettingsGroupSaved, (action) => {
        if (action.payload.isLite === group.lite) {
          afterSaveRef.current?.(action.payload.id);
          afterSaveRef.current = undefined;
          setResetDataI((i) => i + 1);
        }
      }),
      doAddEvent(eventFieldSettingUpdateGroupCheck, (event) => {
        if (
          event.payload.key === eventKeyFieldSettingUpdateGroupCheck.current
        ) {
          dataRef.current.checks[event.payload.data.id] = event.payload.data;
          forceUpdate();
        }
      }),
      doAddEvent(eventReorderScreenDone, (action) => {
        if (action.payload.key === reorderEventKey.current) {
          dataRef.current.fieldIds = action.payload.stringValues;
          forceUpdate();
        }
      }),
    ];
    return () => {
      doRemoveEvents(eventNumbers);
      if (group.lite) {
        dispatch(doFieldSettingsSetLiteGroupId(undefined));
      } else {
        dispatch(doFieldSettingsSetGroupId(undefined));
      }
    };
  }, [dispatch, forceUpdate, group.lite]);

  React.useEffect(() => {
    navigation?.setOptions({ gestureEnabled: !isDirtyVar });
  }, [navigation, isDirtyVar]);
  useEsc(handleClose);

  const handleAddCheck = () => {
    dispatch(
      NAVIGATE_MODAL({
        screen: ModalScreens.FieldSettingsGroupCheck,
        params: {
          eventKey: eventKeyFieldSettingUpdateGroupCheck.current,
          data: {
            id: null,
            name: '',
            typeId: sortBy(Object.values(groupCheckTypes), 'ordering')[0]!.id,
            action: 'create',
          },
        },
      }),
    );
  };

  const handleDeleteCheck = (checkId: string) => {
    const check = dataRef.current.checks[checkId];
    if (check) {
      confirm({
        destructive: true,
        text: 'This cannot be undone',
        title: check.name,
        confirmText: 'Delete',
        onConfirm: () => {
          if (check.action === 'create') {
            delete dataRef.current.checks[checkId];
          } else {
            check.action = 'delete';
          }
          forceUpdate();
        },
      });
    }
  };

  const checksDisplayArray = sortBy(
    Object.values(dataRef.current.checks).reduce<
      ((typeof dataRef.current.checks)[number] & {
        typeName: string;
        ordering: number;
      })[]
    >((acc, check) => {
      if (check.action !== 'delete') {
        const checkType = groupCheckTypes[check.typeId];
        acc.push({
          ...check,
          typeName: checkType?.name || 'Unknown',
          ordering: checkType?.ordering || 0,
        });
      }
      return acc;
    }, []),
    ['name', 'ordering'],
  );

  return (
    <PageScrollView
      header={
        <NavigationHeader
          heading={
            groupId
              ? `${
                  group.lite
                    ? 'Lite Repeatable Group'
                    : dataRef.current.repeatable
                      ? 'Repeatable Group'
                      : 'Group'
                }`
              : `Add ${
                  group.lite
                    ? 'Lite Repeatable Group'
                    : dataRef.current.repeatable
                      ? 'Repeatable Group'
                      : 'Group'
                }`
          }
          onPressCancelButton={handleClose}
          cancelButtonText="Close"
          onPressSaveButton={
            editable
              ? () => handleSave(() => dispatch(NAVIGATE_MODAL_BACK()))
              : undefined
          }
        />
      }
    >
      <Card
        explanation={
          section.managedByOrganisation
            ? `This group is managed by ${section.managedByOrganisation.name} and cannot be modified`
            : ''
        }
      >
        {editable ? (
          <TextField
            value={dataRef.current.name}
            placeholder={group.name || 'Enter a name for this group'}
            onChange={(value) => handleChange('name', value, true)}
            autoFocus={!groupId}
            autoCapitalize="words"
            onEsc={handleClose}
            autoFocusWeb
            autoFocusNative={!groupId}
            autoFocusDelay
          />
        ) : (
          <CardRow
            label={`${dataRef.current.name}${
              dataRef.current.nameSingular
                ? ` (${dataRef.current.nameSingular})`
                : ''
            }`}
          />
        )}
      </Card>

      {dataRef.current.repeatable && editable && (
        <Card
          label="Singular Name"
          explanation="A group called 'Doors' would be 'Door'"
        >
          <TextField
            value={dataRef.current.nameSingular}
            name="nameSingular"
            onChangeWithName={handleChange}
            autoCapitalize="words"
            placeholder={dataRef.current.name || group.name || '...'}
            onEsc={handleClose}
          />
        </Card>
      )}

      {isSupport && (
        <Card label="Identifier">
          {editable ? (
            <TextField
              value={dataRef.current.identifier}
              name="identifier"
              placeholder="Identifier"
              onChangeWithName={handleChange}
              onEsc={handleClose}
              rbs
              autoCorrect={false}
              autoComplete="off"
              autoCapitalize="none"
            />
          ) : (
            <CardRow
              label={dataRef.current.identifier}
              placeholder="No Identifier"
              rbs
            />
          )}
        </Card>
      )}

      {!!navigationRowFields.length && (
        <Card
          label={pluralise(
            navigationRowFields,
            '1 Field',
            '{x} Fields',
            'Fields',
          )}
          style={{ marginBottom: theme.paddingHorizontalHalf }}
          labelLinkText="Reorder"
          onLabelLinkPress={
            fieldIds.length > 1
              ? () =>
                  dispatch(
                    NAVIGATE_MODAL({
                      screen: ModalScreens.REORDER,
                      params: {
                        title: 'Reorder Fields',
                        options: fieldIds.reduce<
                          { value: string; name: string }[]
                        >((acc, fieldId) => {
                          const field = fieldSettings.fields[fieldId];
                          if (field) {
                            acc.push({
                              value: field.id,
                              name: field.name,
                            });
                          }
                          return acc;
                        }, []),
                        eventKey: reorderEventKey.current,
                      },
                    }),
                  )
              : undefined
          }
        >
          {navigationRowFields.map((field) => (
            <CardRow
              key={field.id}
              label={field.label}
              labelAlignTop
              value={field.value}
              onPress={
                field.typeKey === FieldTypeKey.grouplight
                  ? handlePressLightGroupField(field.childGroupId)
                  : handlePressField(field.id)
              }
              icon={field.icon}
              iconPressableStyle={{
                padding: 8,
                marginVertical: -8,
                backgroundColor: '#f1f1f1',
                borderRadius: FIELD_ICON_SIZE - 4,
              }}
              iconSize={FIELD_ICON_SIZE - 4}
            >
              <Txt size="xsmall">
                {!!field.identifier && (
                  <Txt size="xsmall" style={{ color: theme.colors.support }}>
                    {field.identifier}
                  </Txt>
                )}
                {!!field.identifier && !!field.label2 && ' • '}
                {!!field.label2 && <Txt size="xsmall">{field.label2}</Txt>}
              </Txt>
            </CardRow>
          ))}
        </Card>
      )}

      {editable ? (
        <Card label={navigationRowFields.length ? '' : 'Fields'}>
          <ViewWithSeparators
            style={{ flex: 1 }}
            horizontal={screenSize !== ScreenSize.small}
          >
            <CardRow
              style={{ flex: 1 }}
              label="Add field..."
              labelColor={theme.colors.blue}
              icon={ICONS.PlusLight}
              iconColor={theme.colors.blue}
              showMenuOnPress
              hideMenuButton
              onPress={!canSave() ? showCannotSaveAlert : undefined}
              menuOptions={
                canSave()
                  ? [
                      {
                        text: FieldSettingsTypes.note.label,
                        icon: FieldSettingsTypes.note.icon,
                        onPress: () =>
                          handleAddField(FieldSettingsTypes.note.id),
                      },
                      {
                        text: FieldSettingsTypes.options.label,
                        icon: FieldSettingsTypes.options.icon,
                        onPress: () =>
                          handleAddField(FieldSettingsTypes.options.id, false),
                      },
                      {
                        text: FieldSettingsTypes.options.labelMultiSelect!,
                        icon: FieldSettingsTypes.options.iconMultiSelect,
                        onPress: () =>
                          handleAddField(FieldSettingsTypes.options.id, true),
                      },
                      {
                        text: FieldSettingsTypes.number.label,
                        icon: FieldSettingsTypes.number.icon,
                        onPress: () =>
                          handleAddField(FieldSettingsTypes.number.id),
                      },
                      {
                        text: FieldSettingsTypes.file.label,
                        icon: FieldSettingsTypes.file.icon,
                        onPress: () =>
                          handleAddField(FieldSettingsTypes.file.id),
                      },
                      {
                        text: FieldSettingsTypes.image.label,
                        icon: FieldSettingsTypes.image.icon,
                        onPress: () =>
                          handleAddField(FieldSettingsTypes.image.id),
                      },
                      {
                        text: FieldSettingsTypes.date.label,
                        icon: FieldSettingsTypes.date.icon,
                        onPress: () =>
                          handleAddField(FieldSettingsTypes.date.id),
                      },
                      {
                        text: FieldSettingsTypes.time.label,
                        icon: FieldSettingsTypes.time.icon,
                        onPress: () =>
                          handleAddField(FieldSettingsTypes.time.id),
                      },
                      {
                        text: FieldSettingsTypes.pin.label,
                        icon: FieldSettingsTypes.pin.icon,
                        onPress: () =>
                          handleAddField(FieldSettingsTypes.pin.id),
                      },
                      {
                        text: FieldSettingsTypes.qr.label,
                        icon: FieldSettingsTypes.qr.icon,
                        onPress: () => handleAddField(FieldSettingsTypes.qr.id),
                      },
                      {
                        text: FieldSettingsTypes.divider.label,
                        icon: FieldSettingsTypes.divider.icon,
                        onPress: () =>
                          handleAddField(FieldSettingsTypes.divider.id),
                      },
                    ]
                  : undefined
              }
            />
            {!group.lite && (
              <CardRow
                style={{ flex: 1 }}
                label="Add repeatable group..."
                icon={ICONS.PlusLight}
                labelColor={theme.colors.blue}
                iconColor={theme.colors.blue}
                showMenuOnPress
                hideMenuButton
                onPress={!canSave() ? showCannotSaveAlert : undefined}
                menuOptions={
                  canSave()
                    ? [
                        {
                          text: 'New Lite Repeatable',
                          onPress: handleAddLiteRepeatableGroup,
                          icon: ICONS.LayerGroup2Light,
                        },
                        '-',
                        ...availableChildGroups.map((g) => ({
                          text: g.name,
                          subtext: '',
                          onPress: () => handleAddChildRepeatableGroup(g.id),
                          icon: ICONS.LayerGroupLight,
                        })),
                      ].filter(filterArrayEmpty)
                    : undefined
                }
              />
            )}
          </ViewWithSeparators>
        </Card>
      ) : !navigationRowFields.length ? (
        <Card label="Fields">
          <CardRow value="This group has no fields" />
        </Card>
      ) : (
        <View
          style={{
            height: theme.paddingVertical - theme.paddingHorizontalHalf,
          }}
        />
      )}

      {dataRef.current.repeatable && (
        <Card
          label="Checks"
          style={{ marginBottom: theme.paddingHorizontalHalf }}
        >
          {editable || checksDisplayArray.length ? (
            checksDisplayArray.map((check) => (
              <CardRow
                key={check.id}
                label={`${check.name}${
                  check.typeName !== check.name ? ` (${check.typeName})` : ''
                }`}
                onAction={
                  editable ? () => handleDeleteCheck(check.id) : undefined
                }
                actionIcon={ICONS.TrashCanLight}
                onPress={
                  editable
                    ? () =>
                        dispatch(
                          NAVIGATE_MODAL({
                            screen: ModalScreens.FieldSettingsGroupCheck,
                            params: {
                              eventKey:
                                eventKeyFieldSettingUpdateGroupCheck.current,
                              data: {
                                id: check.id,
                                name: check.name,
                                typeId: check.typeId,
                                action: check.action,
                              },
                            },
                          }),
                        )
                    : undefined
                }
              />
            ))
          ) : (
            <CardRow value="This group has no checks" />
          )}
        </Card>
      )}

      {dataRef.current.repeatable &&
        (editable ? (
          <Card>
            <CardRow
              style={{ flex: 1 }}
              label="Add check..."
              labelColor={theme.colors.blue}
              icon={ICONS.PlusLight}
              iconColor={theme.colors.blue}
              onPress={handleAddCheck}
            />
          </Card>
        ) : (
          <View
            style={{
              height: theme.paddingVertical - theme.paddingHorizontalHalf,
            }}
          />
        ))}

      {/* <Card
        label={pluralise(
          usageByOrganisation,
          '1 Organisation',
          '{x} Organisations',
        )}
      >
        {showOrganisationUsage ? (
          usageByOrganisation.map((organisation) => (
            <CardRow
              key={organisation.id}
              label={organisation.name}
              value={pluralise(
                organisation.countResults,
                '1 Result',
                '{x} Results',
                '',
              )}
            />
          ))
        ) : (
          <CardRow
            label={pluralise(
              usageByOrganisation,
              '1 Organisation',
              '{x} Organisations',
              'No Organisations',
            )}
            value="Tap to see usage..."
            onPress={() => setShowOrganisationUsage(true)}
          />
        )}
      </Card> */}

      {isSupport && dataRef.current.repeatable && (
        <Card>
          <CardRow
            label="Disable Auto ID"
            checkedValue={dataRef.current.disableAutoId}
            onPress={
              editable
                ? () =>
                    handleChange(
                      'disableAutoId',
                      !dataRef.current.disableAutoId,
                      true,
                    )
                : undefined
            }
            rbs
          />
          <CardRow
            label="Enable Single PDF Download"
            checkedValue={dataRef.current.allowPdfDownload}
            onPress={
              editable
                ? () =>
                    handleChange(
                      'allowPdfDownload',
                      !dataRef.current.allowPdfDownload,
                      true,
                    )
                : undefined
            }
            rbs
          />
        </Card>
      )}

      {isSupport && editable && !!dataRef.current.repeatable && !group.lite && (
        <Card label="Assessment Pricing">
          <TextField
            textLeft="New"
            textLeftStyle={{ width: 60 }}
            value={group.newCost !== null ? formatCurrency(group.newCost) : ''}
            name="newCost"
            onChangeWithName={handleChangeCost}
            placeholder={`${formatCurrency(
              fieldSettings.defaultCosts.newRepeatable,
            )} (Default)`}
            onEsc={handleClose}
            rbs
          />
          <TextField
            textLeft="Existing"
            textLeftStyle={{ width: 60 }}
            value={
              group.existingCost !== null
                ? formatCurrency(group.existingCost)
                : ''
            }
            onChangeWithName={handleChangeCost}
            name="existingCost"
            placeholder={`${formatCurrency(
              fieldSettings.defaultCosts.existingRepeatable,
            )} (Default)`}
            onEsc={handleClose}
            rbs
          />
        </Card>
      )}

      {!!groupId && editable && (
        <Button
          text={
            group.lite
              ? 'Delete Lite Repeatable Group'
              : dataRef.current.repeatable
                ? 'Delete Repeatable Group'
                : 'Delete Group'
          }
          buttonTheme={ButtonTheme.themeless}
          destructive
          style={{ alignSelf: 'center' }}
          onPress={handleDelete}
        />
      )}
    </PageScrollView>
  );
};
